/**
 * The authored data a run needs, described from the simulation's side.
 *
 * The sim cannot import `@prism/content` (TRD-00 §4.1), so these are structural shapes
 * that the content package's Zod-inferred types satisfy without either package depending
 * on the other. R-06-08b makes this explicit for waves: the director consumes a
 * `WaveDef[]` handed to it in `RunConfig` and must not know whether those waves were
 * authored or generated by TRD-13. The same reasoning covers paths and enemy archetypes.
 *
 * Everything here is data, not behaviour. That is what lets the server rebuild a
 * `RunConfig` from a stored run and replay it (TRD-11 §5).
 */
import type { SynergyEffect } from './effects.js';
import type {
  ClassId,
  DamageKind,
  EnemyId,
  ProjectileKind,
  StatusKind,
  TargetingMode,
  TowerId,
} from './types.js';

/**
 * A tile that changes the tower standing on it (playtest request).
 *
 * The problem this exists for: boards were one clump. Support auras and Beacon buffs have
 * a SMALLER radius than most weapons reach, so the binding constraint on placement was
 * never "can I hit the path" but "am I inside the bubble", and class synergies are global
 * counts with no positional term to pull the other way. There was one right answer per
 * map and every tower took it.
 *
 * A tile holds exactly one tower, where an aura bubble can be shared by ten. That
 * exclusivity is the whole mechanism: scattered tiles turn one global optimum into
 * several local ones, which an aura of any radius cannot do.
 *
 * The mechanics are inlined rather than looked up by `kind`, for the same reason the
 * enemy and tower tables are handed in as data (TRD-00 §4.1): the sim cannot import
 * content, and a run's rules have to travel with the run so the server can replay it.
 * `kind` is presentation only — the client resolves a name, colour and particle from it.
 */
export interface BuffTileSpec {
  x: number;
  y: number;
  /** Authored id, for the client's name/description/colour/particle lookup. */
  kind: string;
  /**
   * The tower here also counts as this class for 2/4/6 thresholds.
   *
   * The strongest of the effects, because its worth is situational rather than flat:
   * nothing at all when you hold no Venom, run-defining at five. Deliberately NOT a
   * damage multiplier — those pay out hardest on whatever already hits hardest, which is
   * the concentration this feature is trying to break, not feed.
   */
  grantsClass?: ClassId | undefined;
  /** Tiles added to the tower's range. A positional buff, so it moves the whole board. */
  rangeBonus?: number | undefined;
  /** Applied on every hit by the tower standing here. */
  onHitStatus?: { status: StatusKind; magnitude: number; durationTicks: number } | undefined;
  /**
   * Restores 1 Core HP every Nth wave cleared. The only payoff here that is not combat,
   * so the question stops being "which tower is strongest on this tile" and becomes "is a
   * life worth a tower in a poor firing position".
   */
  coreHpEveryNWaves?: number | undefined;
}

export interface Point {
  x: number;
  y: number;
}

export interface PathSpec {
  id: number;
  waypoints: readonly Point[];
}

export interface WaveGroupSpec {
  enemy: EnemyId;
  count: number;
  /** Seconds between spawns within the group. */
  spacing: number;
  /** Seconds after wave start. */
  delay: number;
  hpMultiplier: number;
  pathId?: number | undefined;
}

export interface WaveSpec {
  wave: number;
  groups: readonly WaveGroupSpec[];
  isBoss: boolean;
}

export type EnemyFlag = 'flying' | 'splitter' | 'mender' | 'shielded' | 'boss' | 'leaper';

export interface EnemyArchetype {
  id: EnemyId;
  hp: number;
  armor: number;
  speed: number;
  bounty: number;
  leakDamage: number;
  size: number;
  flags: readonly EnemyFlag[];
  splitInto?: { id: EnemyId; count: number } | undefined;
  /**
   * The children TOGETHER inherit this fraction of the parent's max HP, divided evenly.
   *
   * Omitted, a child is simply its own archetype scaled by whatever the parent was scaled
   * by — which is right for the ordinary splitters, whose two children were authored to
   * come out at about 60% of the parent and do. It is useless for a BOSS: the Hive is a
   * 2900 HP boss splitting into six 18 HP sprinters, so a correct scale makes the second
   * phase 3.7% of the first and the swarm stops being a fight at all.
   *
   * A fraction says the thing the design actually means — "the split is worth this much
   * of the boss" — in one number, without a bespoke child archetype per boss whose HP
   * then has to be kept in step with the parent's by hand.
   */
  splitHpFraction?: number | undefined;
  /** Flat absorption subtracted from every hit. NOT the breakable shield bar below —
   *  this one never runs out and never drops. */
  shieldPerHit?: number | undefined;
  /**
   * A breakable shield BAR, on top of `hp` (playtest request, Prism's finale).
   *
   * While it holds, every hit lands on the shield and `hp` cannot be touched at all.
   * When it reaches zero the enemy drops it and freezes for `shieldDownTicks` — that
   * window is the only time its real health is reachable — then the shield comes back
   * to full and it moves again. Repeat until dead.
   *
   * The two fields only mean anything together, so the content schema requires both.
   */
  shieldHp?: number | undefined;
  shieldDownTicks?: number | undefined;
  healRadius?: number | undefined;
  healPercentPerSecond?: number | undefined;
  /** `leaper` (backlog B-16): every `leapEveryTicks` the enemy jumps `leapTiles` forward
   *  along its path. Keyed off the enemy's own age, and blocked by stun/freeze like any
   *  other movement — freezing the hop is the counterplay. */
  leapEveryTicks?: number | undefined;
  leapTiles?: number | undefined;
  /**
   * Statuses this archetype simply cannot be given. Prism's stun/freeze immunity was
   * described in the content comments from day one and never actually existed — every
   * "immune" boss could be frozen solid by Frost-6 like anything else.
   */
  statusImmunities?: readonly StatusKind[] | undefined;
  /**
   * Ticks spent rising out of the ground on spawn. While emerging the enemy does not
   * move and cannot be damaged — it is an entrance, not a free kill. Zero for everything
   * that simply walks on.
   */
  emergeTicks?: number | undefined;
  /**
   * Every N ticks of its own age, blink to the mirrored side of the map (top↔bottom).
   * The roll is seeded, so a given day's Prism always cheats the same way.
   */
  teleportEveryTicks?: number | undefined;
  /**
   * Disabler (playtest request): every `disableEveryTicks` it fires a visible line at the
   * strongest tower within `disableRange`, and every tower within `disableRadius` of that
   * point stops firing for `disableTicks`.
   *
   * The reach is deliberately not drawn anywhere — you learn it by being hit — but the
   * shot itself is loud, or a tower going dark reads as a bug rather than as an attack.
   */
  /**
   * A plate across its front (playtest request): a hit arriving within `acos(frontalCos)`
   * of dead ahead lands for `1 - frontalReduction` of its damage.
   *
   * Authored as a COSINE rather than an angle because the sim may not call `cos` — the
   * determinism contract forbids it — and because a cosine computed in content at module
   * load could differ in its last bits between the client's engine and the server's, which
   * is a hash mismatch dressed up as a rounding error.
   *
   * The arc is deliberately narrow. A wide one averages out: an enemy walking past a tower
   * spends about half its time in that circle approaching and half departing, so a broad
   * plate is just "tougher" with extra arithmetic and teaches nothing. Narrow, it means
   * something specific — do not line your towers up facing down the lane.
   */
  frontalCos?: number | undefined;
  frontalReduction?: number | undefined;
  disableRange?: number | undefined;
  disableRadius?: number | undefined;
  disableTicks?: number | undefined;
  disableEveryTicks?: number | undefined;
}

/** Archetypes by id. A missing id is a content bug and spawns nothing. */
export type EnemyTable = Readonly<Record<EnemyId, EnemyArchetype>>;

export function hasFlag(archetype: EnemyArchetype, flag: EnemyFlag): boolean {
  return archetype.flags.includes(flag);
}

// ── Towers and balance (TRD-07) ───────────────────────────────────────────────────────

export type TowerTier = 1 | 2 | 3;

/** A tower definition, reduced to what the simulation needs to place and fire one. */
export interface TowerSpec {
  id: TowerId;
  tier: TowerTier;
  /**
   * Exactly two, distinct (TRD-02 R-02-06).
   *
   * The synergy engine counts these, so without them the class thresholds that are the
   * entire point of the game cannot be evaluated.
   */
  classes: readonly ClassId[];
  base: {
    damage: number;
    damageKind: DamageKind;
    attackInterval: number;
    range: number;
    projectile: {
      kind: ProjectileKind;
      speed: number;
      radius: number;
      turnRate: number;
      aoeRadius: number;
      /**
       * Firing approach (TRD-05 §5.1). Optional so a hand-built spec — a test fixture,
       * a scenario file written before these existed — still describes a valid tower;
       * `applyTowerSpec` supplies the same identity defaults the Zod schema does.
       */
      salvo?: number;
      spreadRadians?: number;
      burstCount?: number;
      burstIntervalTicks?: number;
      pierce?: number;
      acceleration?: number;
      beamRampPerTick?: number;
      beamRampMax?: number;
      /** `zone` kind only (B-18/B-19): telegraph, active window, strike cadence, drift. */
      zoneWindupTicks?: number;
      zoneActiveTicks?: number;
      zoneStrikeEveryTicks?: number;
      zoneTrackSpeed?: number;
    };
    defaultTargeting: TargetingMode;
  };
  starScaling: readonly [1, number, number];
  /**
   * How RANGE grows with stars, weighted by the same role `starScaling` uses (playtest
   * request). A merge that only ever added damage left a badly-placed tower badly placed;
   * widening its reach is what makes merging feel like an upgrade to the BOARD rather
   * than to one number.
   *
   * Optional so a hand-built spec (a test fixture) still describes a valid tower.
   */
  rangeScaling?: readonly [1, number, number] | undefined;
  /**
   * A pure buffer (backlog B-15): never targets, never fires. Authored with
   * `damage: 0` and `attackInterval: 0`; its power is entirely `innateEffects`,
   * and its cost is the board slot and class counts it occupies.
   */
  support?: boolean;
  /**
   * The spec's authored identity, evaluated at synergy recompute as if the tower were
   * the sole member of its own class (backlog B-15/B-17): a support tower's aura, a
   * broodmother's innate `summon`. Applied AFTER the class tier pass so
   * replace-semantics entries (summon) let the authored identity win on its own tower.
   */
  innateEffects?: readonly SynergyEffect[];
}

export type TowerTable = Readonly<Record<TowerId, TowerSpec>>;

/**
 * A run-wide effect bundle applied once at synergy recompute (TRD-08 R-08-22b).
 *
 * Two producers, one consumer: the daily MUTATOR and the meta tree's effect nodes both
 * compile to this, and the synergy engine evaluates it with zero new evaluation code —
 * which was R-08-22b's whole test of whether the effect union was designed right.
 *
 * `forClass` scopes the bundle to one class's placed towers (a meta node like "Frost
 * towers +10% damage"); omitted, it applies globally. Travels with the RunConfig for
 * the same reason as everything else in this file: the server re-simulates from stored
 * data, so every rule a run obeyed has to travel with the run.
 */
export interface RunEffect {
  forClass?: ClassId;
  /** Where this bundle came from — a tree node name, a mutator name. Shown in the
   *  inspector's stat breakdown (R-09-21); "Meta" when absent. */
  label?: string;
  effects: readonly SynergyEffect[];
}

// ── Classes (TRD-08) ──────────────────────────────────────────────────────────────────

export interface ClassTierSpec {
  threshold: 2 | 4 | 6;
  /** One line, shown in the synergy panel (TRD-09 R-09-18). */
  summary: string;
  effects: readonly SynergyEffect[];
}

export interface ClassSpec {
  id: ClassId;
  name: string;
  tiers: readonly [ClassTierSpec, ClassTierSpec, ClassTierSpec];
}

/**
 * Class definitions by id.
 *
 * Handed in as data for the same reason as everything else here: the server rebuilds a
 * `RunConfig` from a stored submission and re-simulates it, so every rule a run obeyed
 * has to travel with the run rather than being looked up from a package the server may
 * not have (TRD-11 §2).
 */
export type ClassTable = Readonly<Record<string, ClassSpec>>;

/**
 * TRD-07 §3 — every balance constant, handed in rather than hardcoded.
 *
 * `BALANCE` in `@prism/content` is the authority; this is the shape it satisfies. A
 * magic number inside a sim system cannot be tuned without a code change and hides from
 * balance review, which is why TRD-07's acceptance criteria forbid one.
 */
export interface BalanceSpec {
  REROLL_BASE_COST: number;
  REROLL_ESCALATION: number;
  INTEREST_RATE: number;
  INTEREST_CAP: number;
  SELL_REFUND_RATE: number;
  MERGE_COUNT: number;
  WAVE_PAYOUT_BASE: number;
  /**
   * Hard ceiling on PLACED towers.
   *
   * The constraint the design was missing. Without it, income outruns the placement
   * budget roughly three to one and the surplus flows into free merges — so nothing ever
   * forces a player to choose what stays on the board. At 10 towers there are 20
   * class-slots: about two tier-6 synergies and a tier-4, or a thin spread of tier-2s
   * across everything. Either is a commitment.
   */
  MAX_PLACED_TOWERS: number;
  TOWER_COST: Readonly<Record<TowerTier, number>>;
  SHOP_TIER_WEIGHTS: readonly { maxWave: number; weights: readonly number[] }[];
}

/**
 * The upper bound of the last shop-tier row — "every wave from here on".
 *
 * Deliberately NOT `Infinity`. A run config travels to the client as JSON from
 * `/api/daily/:date`, and `JSON.stringify(Infinity)` is `null`; the client would then
 * evaluate `wave <= null`, which is false at every wave, and roll its shop from a
 * different row than the server replays with. Today that happens to be survivable because
 * the unbounded row is last and the lookup falls back to the last row anyway — an
 * accident, not a guarantee, and one that stops holding the moment a row is added after
 * it. A finite sentinel round-trips exactly and needs no such luck.
 */
export const UNBOUNDED_WAVE = Number.MAX_SAFE_INTEGER;

/**
 * Values a world falls back to when no balance is supplied.
 *
 * Only so a bare `makeRunConfig()` constructs something coherent for the chassis tests.
 * A real run is handed `BALANCE` from the content package, and these must mirror it.
 */
export const DEFAULT_BALANCE: BalanceSpec = {
  REROLL_BASE_COST: 2,
  REROLL_ESCALATION: 1,
  INTEREST_RATE: 0.1,
  INTEREST_CAP: 5,
  SELL_REFUND_RATE: 0.6,
  MERGE_COUNT: 3,
  WAVE_PAYOUT_BASE: 10,
  MAX_PLACED_TOWERS: 10,
  TOWER_COST: { 1: 3, 2: 5, 3: 8 },
  SHOP_TIER_WEIGHTS: [
    { maxWave: 3, weights: [100, 0, 0] },
    { maxWave: 7, weights: [70, 30, 0] },
    { maxWave: 12, weights: [45, 42, 13] },
    { maxWave: 18, weights: [25, 45, 30] },
    { maxWave: UNBOUNDED_WAVE, weights: [15, 40, 45] },
  ],
};
